import { queryOptions } from '@tanstack/react-query';

import { getClipsClipId } from '~/shared/api/generated/endpoints/clip/clip';
import { getCustomerIdExerciseExerciseIdClips } from '~/shared/api/generated/endpoints/customer-clip/customer-clip';
import type {
  TrainWithMeModulesAccountsDtosPersonRef as PersonDto,
  TrainWithMeModulesClipsDtosResponsesClipAnchorResponse as AnchorDto,
  TrainWithMeModulesClipsDtosResponsesClipCommentResponse as CommentDto,
  TrainWithMeModulesClipsDtosResponsesClipEntryResponse as ClipDto,
} from '~/shared/api/generated/model';
import { civilDayInstant, parseInstant } from '~/shared/lib/calendarDate';

import type { ClipAnchor, ClipComment, ClipEntry, ClipPerson } from '../model/clipEntry';

/*
 * Both reads mint SIGNED media urls, fresh on every answer. A refetch on window focus would swap the
 * `src` of a playing film and restart it at 0:00, so the answer is held until something invalidates
 * it (a comment, a seen mark) or the trainer asks again.
 */
const SIGNED = { staleTime: Number.POSITIVE_INFINITY, refetchOnWindowFocus: false } as const;

const clipRoot = ['exercise-clip'] as const;

export function clipTimelineQueryKey(clientId: number, exerciseId: number) {
  return [...clipRoot, 'timeline', clientId, exerciseId] as const;
}

export function clipTimelineQueryOptions(clientId: number, exerciseId: number) {
  return queryOptions({
    queryKey: clipTimelineQueryKey(clientId, exerciseId),
    queryFn: async ({ signal }) =>
      toClipEntries(
        (await getCustomerIdExerciseExerciseIdClips(clientId, exerciseId, undefined, signal)).clips,
      ),
    ...SIGNED,
  });
}

function clipQueryKey(clipId: number) {
  return [...clipRoot, 'one', clipId] as const;
}

/** One clip's thread, paged; the player owns the read and every write patches this key. */
export function clipCommentsQueryKey(clipId: number) {
  return [...clipRoot, 'comments', clipId] as const;
}

export function clipQueryOptions(clipId: number) {
  return queryOptions({
    queryKey: clipQueryKey(clipId),
    queryFn: async ({ signal }) => toClipEntry(await getClipsClipId(clipId, undefined, signal)),
    ...SIGNED,
  });
}

export function toClipPerson(dto: PersonDto | null | undefined): ClipPerson {
  return {
    userId: dto?.userId ?? null,
    fullName: dto?.fullName ?? '',
    initials: dto?.initials ?? '',
    avatarUrl: dto?.avatarUrl ?? null,
  };
}

/** The set a recording is anchored to, or `null` for one filmed outside a session. */
export function toClipAnchor(dto: AnchorDto | null | undefined): ClipAnchor | null {
  if (dto?.workoutExecutionId === null || dto?.workoutExecutionId === undefined) return null;
  return {
    workoutExecutionId: dto.workoutExecutionId,
    setId: dto.setId ?? null,
    setNumber: dto.setNumber ?? null,
    weightKg: dto.weight ?? null,
    reps: dto.reps ?? null,
  };
}

export function toClipComment(dto: CommentDto): ClipComment | null {
  if (dto.id === undefined) return null;
  return {
    id: dto.id,
    author: toClipPerson(dto.author),
    body: dto.body ?? '',
    createdAt: parseInstant(dto.createdAt) ?? 0,
    editedAt: parseInstant(dto.editedAt),
    offsetMs: dto.offsetMs ?? null,
  };
}

export function toClipEntry(dto: ClipDto): ClipEntry | null {
  if (dto.id === undefined || dto.exerciseId === undefined) return null;
  const comments = (dto.discussion?.entries ?? []).flatMap((entry) => {
    const comment = toClipComment(entry);
    return comment === null ? [] : [comment];
  });
  return {
    id: dto.id,
    customerId: dto.customerId ?? null,
    exerciseId: dto.exerciseId,
    performedOn: civilDayInstant(dto.performedOn ?? '') ?? 0,
    createdAt: parseInstant(dto.createdAt) ?? 0,
    author: toClipPerson(dto.author),
    unread: dto.unread === true,
    expiresSoon: dto.expiresSoon === true,
    expiresAt: parseInstant(dto.expiresAt),
    anchor: toClipAnchor(dto.anchor),
    media: {
      width: dto.media?.width ?? null,
      height: dto.media?.height ?? null,
      durationSeconds: dto.media?.durationSeconds ?? null,
      posterUrl: dto.media?.posterUrl ?? null,
      videoUrl: dto.media?.videoUrl ?? null,
    },
    comments,
  };
}

function toClipEntries(dtos: readonly ClipDto[] | null | undefined): readonly ClipEntry[] {
  return (dtos ?? []).flatMap((dto) => {
    const entry = toClipEntry(dto);
    return entry === null ? [] : [entry];
  });
}
